/* @moldura padrao */
/**
 * page.tsx (ordens-servico) — Lista de OS com filtros. Tema AdminUX (Bootstrap).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { CustomMolduraPagina } from '@/components/CustomMolduraPagina';
import { api, ApiError } from '@/lib/api';
import { formatarData } from '@/lib/data';
import { useAuth } from '@/auth/auth-context';
import type {
  Cobranca,
  OSListItem,
  StatusOS,
  TipoOS,
} from './shared';
import {
  COBRANCAS,
  STATUS,
  caminhoDaLoja,
  TIPOS,
  rotuloTipo,
  varianteCobranca,
  varianteStatus,
} from './shared';

const TODOS = 'todos';

// Variantes semânticas do shared -> classes de badge do Bootstrap.
function badge(v: string): string {
  const mapa: Record<string, string> = {
    success: 'text-bg-success',
    warning: 'text-bg-warning',
    destructive: 'text-bg-danger',
    secondary: 'text-bg-secondary',
  };
  return mapa[v] ?? 'text-bg-secondary';
}

// Ícone + cor por tipo de serviço.
// A cor é o PAPEL, não o tom: `estilos/_cores.scss` decide qual azul é
// instalação, e decide de novo no modo escuro. Antes daqui havia três hex
// cravados — e o azul de instalação divergia do da agenda sem ninguém ver.
const ICONE_TIPO: Record<TipoOS, { icon: string; cor: string }> = {
  instalacao: { icon: 'bi-hammer', cor: 'var(--casaa-tipo-instalacao)' },
  manutencao: { icon: 'bi-wrench', cor: 'var(--casaa-tipo-manutencao)' },
  lavagem: { icon: 'bi-droplet', cor: 'var(--casaa-tipo-lavagem)' },
};

// Rótulos curtos e em minúsculas para a lista.
const ST_CURTO: Record<StatusOS, string> = {
  aberta: 'aberta',
  agendada: 'agendada',
  em_andamento: 'andamento',
  concluida: 'concluída',
  cancelada: 'cancelada',
};
const COB_CURTO: Record<Cobranca, string> = {
  a_definir: 'definir',
  sim: 'sim',
  nao: 'não',
};
// Status de autorização derivado do módulo geral (badge curto na lista).
const ST_AUT: Record<string, { label: string; variante: string }> = {
  pendente: { label: 'pendente', variante: 'warning' },
  autorizada: { label: 'autorizada', variante: 'success' },
  negada: { label: 'negada', variante: 'destructive' },
  cancelada: { label: 'cancelada', variante: 'secondary' },
};

// Vendedor: só o primeiro nome, em minúsculas.
const primeiroNome = (n?: string | null) =>
  (n || '').trim().split(/\s+/)[0]?.toLowerCase() || '—';

// Colunas da tabela (ordenáveis). `w` = largura mínima (encolhe a coluna).
// Larguras fixas (table-layout: fixed). Cliente fica SEM largura -> absorve todo
// o espaço restante (é a coluna mais larga). Cidade tem largura própria.
const COLS: {
  key: keyof OSListItem;
  label: string;
  cls?: string;
  w?: string;
}[] = [
  // Larguras em % (somam ~100%) -> o espaço é distribuído entre as colunas.
  { key: 'codigo', label: 'Código', cls: 'text-nowrap', w: '9%' },
  { key: 'tipo', label: 'Tipo', cls: 'text-center', w: '4%' },
  { key: 'cliente_nome', label: 'Cliente', w: '26%' }, // maior + reticências
  { key: 'vendedor', label: 'Vendedor', cls: 'text-nowrap', w: '9%' },
  { key: 'cidade', label: 'Cidade', w: '14%' },
  { key: 'pedido_venda', label: 'Pedido', cls: 'text-nowrap', w: '6%' },
  // somem em telas estreitas nesta ordem: autoriz. -> cobrança -> status
  { key: 'status', label: 'Status', cls: 'd-none d-lg-table-cell', w: '8%' },
  { key: 'cobranca', label: 'Cobrança', cls: 'd-none d-xl-table-cell', w: '8%' },
  { key: 'autorizacao_status', label: 'Autoriz.', cls: 'd-none d-xxl-table-cell', w: '8%' },
  { key: 'dt_abertura', label: 'Abertura', cls: 'text-nowrap', w: '8%' },
];

export function OrdensServicoPage() {
  const { pode, usuario } = useAuth();
  const navigate = useNavigate();
  /* A LOJA SAI DO CAMINHO, não de um select: cada loja é uma página
     (`/ordens-servico/loja/bz`). Só vale loja que está em `lojas_os` — as que
     o âmbito dá à pessoa. Caminho sem loja, ou com loja que não é dela, leva à
     primeira que ela tem (ver o `Navigate` lá embaixo). A trava real continua
     no servidor: a lista já sai filtrada pelo âmbito. */
  const { loja: codigoLoja } = useParams();
  const lojasOS = usuario?.lojas_os ?? [];
  const loja = lojasOS.find((l) => l.codigo.toLowerCase() === codigoLoja?.toLowerCase());
  const [lista, setLista] = useState<OSListItem[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  /* ATIVAS × ARQUIVADAS (29/10/2026) — o mesmo par de abas da lista de
     despesas ("Em uso / Arquivadas"). Arquivar não apaga: tira da vista do dia
     a dia, e a aba é onde se acha a OS de volta. O servidor já separa
     (`arquivada` omitido = só as não arquivadas). */
  const [vista, setVista] = useState<'ativas' | 'arquivadas'>('ativas');
  const [fStatus, setFStatus] = useState(TODOS);
  const [fTipo, setFTipo] = useState(TODOS);
  const [fCobranca, setFCobranca] = useState(TODOS);
  const [busca, setBusca] = useState('');

  // ordenação + paginação (client-side sobre a lista já filtrada)
  const [ordCol, setOrdCol] = useState<keyof OSListItem>('dt_abertura');
  const [ordAsc, setOrdAsc] = useState(false);
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(20);

  async function carregar() {
    if (!loja) return;
    setCarregando(true);
    setErro(null);
    try {
      const params = new URLSearchParams();
      params.set('loja_id', String(loja.id));
      if (vista === 'arquivadas') params.set('arquivada', 'true');
      if (fStatus !== TODOS) params.set('status_os', fStatus);
      if (fTipo !== TODOS) params.set('tipo', fTipo);
      if (fCobranca !== TODOS) params.set('cobranca', fCobranca);
      if (busca.trim()) params.set('busca', busca.trim());
      const qs = params.toString();
      setLista(await api.get<OSListItem[]>(`/ordens-servico${qs ? `?${qs}` : ''}`));
    } catch (err) {
      setErro(
        err instanceof ApiError ? err.message : 'Não foi possível carregar as OS.',
      );
    } finally {
      setCarregando(false);
    }
  }

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loja?.id, vista, fStatus, fTipo, fCobranca]);

  /**
   * Busca instantânea: recarrega sozinha enquanto se digita, sem Enter. O atraso
   * de 300ms espera a pessoa parar de teclar — sem ele seria uma requisição por
   * caractere. Pula a 1ª execução (montagem) pra não duplicar a carga inicial que
   * o efeito dos filtros acima já faz, e volta pra página 1 porque o resultado
   * encolhe (senão a paginação para numa página que não existe mais).
   */
  const buscaMontou = useRef(false);
  useEffect(() => {
    if (!buscaMontou.current) {
      buscaMontou.current = true;
      return;
    }
    const t = setTimeout(() => {
      setPagina(1);
      carregar();
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busca]);

  const ordenada = useMemo(() => {
    const arr = [...lista];
    arr.sort((a, b) => {
      const va = a[ordCol];
      const vb = b[ordCol];
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      const c = String(va).localeCompare(String(vb), 'pt-BR', { numeric: true });
      return ordAsc ? c : -c;
    });
    return arr;
  }, [lista, ordCol, ordAsc]);

  const totalPaginas = Math.max(1, Math.ceil(ordenada.length / porPagina));
  const paginaAtual = Math.min(pagina, totalPaginas);
  const visivel = ordenada.slice(
    (paginaAtual - 1) * porPagina,
    paginaAtual * porPagina,
  );

  // volta pra 1ª página quando muda a lista, ordenação ou tamanho
  useEffect(() => {
    setPagina(1);
  }, [lista, ordCol, ordAsc, porPagina]);

  function ordenarPor(col: keyof OSListItem) {
    if (ordCol === col) setOrdAsc((a) => !a);
    else {
      setOrdCol(col);
      setOrdAsc(true);
    }
  }

  if (!loja) {
    return lojasOS.length > 0 ? (
      <Navigate to={caminhoDaLoja(lojasOS[0].codigo)} replace />
    ) : (
      <CustomMolduraPagina titulo="Ordens de Serviço" semCaminho>
        <div className="py-5 text-center text-secondary">
          Nenhuma loja liberada para você ver ordens de serviço.
        </div>
      </CustomMolduraPagina>
    );
  }

  return (
    <CustomMolduraPagina
      titulo={`Ordens de Serviço — ${loja.nome_curto || loja.nome}`}
      descricao="Instalações, manutenções e lavagens"
      semCaminho
      telaCheia
      busca={{
        valor: busca,
        aoMudar: setBusca,
        placeholder: 'Buscar OS, cliente ou pedido…',
      }}
      acoes={
        // Sem `ordens-servico.os.criar` o formulário abria e não tinha como salvar.
        pode('ordens-servico.os.criar') ? (
          <button className="btn btn-theme" onClick={() => navigate(`/ordens-servico/nova?loja=${loja.codigo.toLowerCase()}`)}>
            <i className="bi bi-plus-lg me-1"></i> Nova OS
          </button>
        ) : undefined
      }
    >
      <ul className="nav nav-tabs adminuiux-tabs mt-3 mb-3">
        {([
          ['ativas', 'Ativas', 'bi-list-check'],
          ['arquivadas', 'Arquivadas', 'bi-archive'],
        ] as const).map(([k, rotulo, icone]) => (
          <li className="nav-item" key={k}>
            <button
              type="button"
              className={`nav-link ${vista === k ? 'active' : ''}`}
              onClick={() => setVista(k)}
            >
              <i className={`bi ${icone} me-1`}></i>
              {rotulo}
            </button>
          </li>
        ))}
      </ul>

      <div className="card adminuiux-card">
        <div className="card-body">
          {/* Filtros */}
          <div className="row g-2 mb-3">
            <div className="col-6 col-md-4 col-lg-auto">
              <select
                className="form-select form-select-sm"
                value={fStatus}
                onChange={(e) => setFStatus(e.target.value)}
              >
                <option value={TODOS}>Todos os status</option>
                {STATUS.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-6 col-md-4 col-lg-auto">
              <select
                className="form-select form-select-sm"
                value={fTipo}
                onChange={(e) => setFTipo(e.target.value)}
              >
                <option value={TODOS}>Todos os tipos</option>
                {TIPOS.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-6 col-md-4 col-lg-auto">
              <select
                className="form-select form-select-sm"
                value={fCobranca}
                onChange={(e) => setFCobranca(e.target.value)}
              >
                <option value={TODOS}>Cobrança: todas</option>
                {COBRANCAS.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Tabela */}
          {carregando ? (
            <div className="d-flex align-items-center justify-content-center gap-2 py-5 text-secondary">
              <span className="spinner-border spinner-border-sm" /> Carregando…
            </div>
          ) : erro ? (
            <div className="py-5 text-center text-danger">{erro}</div>
          ) : (
            <>
            {/* CELULAR: LISTA, NÃO TABELA (29/10/2026). Em 390px a tabela de dez
                colunas encavalava código, cliente e cidade — e é aqui que o
                instalador acha a OS (ensaio, print 22). Cada linha diz o que
                se procura com o dedo: tipo, código, cliente, cidade e data. */}
            <div className="list-group list-group-flush d-md-none">
              {visivel.map((o) => (
                <button
                  type="button"
                  key={o.id}
                  className="list-group-item list-group-item-action px-0 py-3"
                  onClick={() => navigate(`/ordens-servico/${o.id}`)}
                >
                  <div className="d-flex align-items-center gap-2">
                    <i
                      className={`bi ${ICONE_TIPO[o.tipo].icon}`}
                      style={{ color: ICONE_TIPO[o.tipo].cor }}
                      title={rotuloTipo(o.tipo)}
                    ></i>
                    <span className="fw-semibold">{o.codigo}</span>
                    <span className={`badge ${badge(varianteStatus(o.status))} ms-auto`}>
                      {ST_CURTO[o.status]}
                    </span>
                  </div>
                  <div className="text-truncate mt-1">{o.cliente_nome}</div>
                  <div className="d-flex gap-2 small text-secondary mt-1">
                    <span className="text-truncate">{o.cidade ?? '—'}</span>
                    {o.pedido_venda && <span className="text-nowrap">· PV {o.pedido_venda}</span>}
                    <span className="text-nowrap ms-auto">
                      {o.dt_abertura ? formatarData(o.dt_abertura) : '—'}
                    </span>
                  </div>
                </button>
              ))}
              {ordenada.length === 0 && (
                <div className="py-5 text-center text-secondary">
                  {vista === 'arquivadas'
                    ? 'Nenhuma OS arquivada nesta loja.'
                    : 'Nenhuma OS encontrada.'}
                </div>
              )}
            </div>
            <div className="table-responsive d-none d-md-block">
              <table
                className="table align-middle table-hover mb-0"
                style={{ fontSize: '0.9rem', tableLayout: 'fixed', width: '100%' }}
              >
                <thead>
                  <tr
                    className="text-secondary text-uppercase"
                    style={{ fontSize: '0.75rem' }}
                  >
                    {COLS.map((c) => (
                      <th
                        key={c.key}
                        className={`user-select-none ${c.cls ?? ''}`}
                        style={{ cursor: 'pointer', width: c.w }}
                        onClick={() => ordenarPor(c.key)}
                      >
                        {c.label}
                        {ordCol === c.key && (
                          <i
                            className={`bi ${ordAsc ? 'bi-caret-up-fill' : 'bi-caret-down-fill'} ms-1`}
                            style={{ fontSize: '0.6rem' }}
                          ></i>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visivel.map((o) => (
                    <tr
                      key={o.id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/ordens-servico/${o.id}`)}
                    >
                      <td className="fw-semibold text-nowrap">{o.codigo}</td>
                      <td className="text-center">
                        <i
                          className={`bi ${ICONE_TIPO[o.tipo].icon}`}
                          style={{ color: ICONE_TIPO[o.tipo].cor }}
                          title={rotuloTipo(o.tipo)}
                        ></i>
                      </td>
                      <td className="text-truncate" title={o.cliente_nome}>
                        {o.cliente_nome}
                      </td>
                      <td
                        className="text-secondary text-nowrap"
                        title={o.vendedor ?? ''}
                      >
                        {primeiroNome(o.vendedor)}
                      </td>
                      <td
                        className="text-secondary text-truncate"
                        title={o.cidade ?? ''}
                      >
                        {o.cidade ?? '—'}
                      </td>
                      <td className="text-secondary text-nowrap">
                        {o.pedido_venda ?? '—'}
                      </td>
                      <td className="text-nowrap d-none d-lg-table-cell">
                        <span className={`badge ${badge(varianteStatus(o.status))}`}>
                          {ST_CURTO[o.status]}
                        </span>
                      </td>
                      <td className="text-nowrap d-none d-xl-table-cell">
                        <span
                          className={`badge ${badge(varianteCobranca(o.cobranca))}`}
                        >
                          {COB_CURTO[o.cobranca]}
                        </span>
                      </td>
                      <td className="text-nowrap d-none d-xxl-table-cell">
                        {o.autorizacao_status ? (
                          <span
                            className={`badge ${badge(
                              ST_AUT[o.autorizacao_status]?.variante ?? 'secondary',
                            )}`}
                          >
                            {ST_AUT[o.autorizacao_status]?.label ??
                              o.autorizacao_status}
                          </span>
                        ) : (
                          <span className="text-secondary">—</span>
                        )}
                      </td>
                      <td className="text-secondary text-nowrap">
                        {o.dt_abertura ? formatarData(o.dt_abertura) : '—'}
                      </td>
                    </tr>
                  ))}
                  {ordenada.length === 0 && (
                    <tr>
                      <td colSpan={10} className="py-5 text-center text-secondary">
                        {vista === 'arquivadas'
                          ? 'Nenhuma OS arquivada nesta loja.'
                          : 'Nenhuma OS encontrada.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            </>
          )}

          {/* Paginação */}
          {!carregando && !erro && ordenada.length > 0 && (
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mt-3">
              <div className="d-flex align-items-center gap-2 text-secondary small">
                <span>Por página</span>
                <select
                  className="form-select form-select-sm w-auto"
                  value={porPagina}
                  onChange={(e) => setPorPagina(Number(e.target.value))}
                >
                  {[20, 40, 80].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
                <span>{ordenada.length} registro(s)</span>
              </div>
              <div className="d-flex align-items-center gap-2">
                <button
                  className="btn btn-sm btn-outline-theme"
                  disabled={paginaAtual <= 1}
                  onClick={() => setPagina((p) => Math.max(1, p - 1))}
                >
                  <i className="bi bi-chevron-left"></i>
                </button>
                <span className="small text-secondary">
                  Página {paginaAtual} de {totalPaginas}
                </span>
                <button
                  className="btn btn-sm btn-outline-theme"
                  disabled={paginaAtual >= totalPaginas}
                  onClick={() => setPagina((p) => Math.min(totalPaginas, p + 1))}
                >
                  <i className="bi bi-chevron-right"></i>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </CustomMolduraPagina>
  );
}